import { clsx } from 'clsx'
import { useId } from 'react'

type Props = {
  checked: boolean
  onChange: (next: boolean) => void
  label: string
  /** Secondary text shown under the label. */
  description?: string
  /** Hide the visible label (still announced). */
  labelHidden?: boolean
  disabled?: boolean
  size?: 'md' | 'sm'
  className?: string
}

/** Accessible switch. Label is always associated; state is conveyed by position and aria-checked. */
export function Toggle({ checked, onChange, label, description, labelHidden, disabled, size = 'md', className }: Props) {
  const id = useId()
  const descId = description ? `${id}-desc` : undefined
  return (
    <div className={clsx('flex items-start gap-3', className)}>
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={descId}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={clsx(
          'relative shrink-0 border transition-colors duration-150',
          size === 'md' ? 'h-6 w-11' : 'h-5 w-9',
          checked ? 'bg-ink border-ink' : 'bg-gray-300 border-gray-300',
          disabled && 'opacity-40 cursor-not-allowed',
        )}
      >
        <span
          aria-hidden="true"
          className={clsx(
            'absolute top-0.5 left-0.5 bg-paper shadow-knob transition-transform duration-150 ease-out-soft',
            size === 'md' ? 'h-[1.125rem] w-[1.125rem]' : 'h-3.5 w-3.5',
            checked && (size === 'md' ? 'translate-x-5' : 'translate-x-4'),
          )}
        />
        <span className="sr-only">{label}</span>
      </button>
      <label htmlFor={id} className={clsx('cursor-pointer', labelHidden && 'sr-only')}>
        <span className="block text-body text-ink">{label}</span>
        {description && (
          <span id={descId} className="block text-small text-dim">
            {description}
          </span>
        )}
      </label>
    </div>
  )
}
